"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { ReactNode } from "react";
import { useQuery, useMutation } from "convex/react";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import { api } from "../../convex/_generated/api";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";

const COOKIE_NAME = "convex-session";

function getSessionTokenFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function clearSessionCookie() {
  document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// Subscribe to cookie changes (via storage events or polling)
function subscribeToCookie(callback: () => void) {
  // Check cookie on visibility change (handles redirect return)
  const handler = () => callback();
  document.addEventListener("visibilitychange", handler);
  return () => document.removeEventListener("visibilitychange", handler);
}

function getServerSnapshot(): string | null {
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConvexUser = Record<string, any>;

interface SessionContextValue {
  user: ConvexUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionToken: string | null;
  /** Convex user query unanswered for >20s — network problem, session still valid */
  connectionStalled: boolean;
  logout: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const { paymentsEnabled } = useSiteFlavor();
  const sessionToken = useSyncExternalStore(
    subscribeToCookie,
    getSessionTokenFromCookie,
    getServerSnapshot
  );

  // Real-time user data from Convex
  const user = useQuery(
    api.telegramAuthQueries.getCurrentUser,
    sessionToken ? { sessionToken } : "skip"
  );

  // Track Convex connection stalls: if the user query hasn't answered after 20s
  // we surface a "connection problems" state — but we NEVER treat a stall as an
  // expired session. Clearing the cookie on a stall (the old behaviour) logged
  // users with perfectly valid 30-day sessions out whenever their network path
  // to Convex degraded (RU mobile links, flaky VPNs). The timer is cleared as
  // soon as `user` resolves, so a query that eventually answers never stalls.
  const [queryStalled, setQueryStalled] = useState(false);
  useEffect(() => {
    if (!sessionToken || user !== undefined) {
      setQueryStalled(false);
      return;
    }
    const timer = setTimeout(() => setQueryStalled(true), 20_000);
    return () => clearTimeout(timer);
  }, [sessionToken, user]);

  // Expired = the server explicitly said "no user for this token".
  const isExpired = sessionToken !== null && user === null;
  const connectionStalled =
    sessionToken !== null && user === undefined && queryStalled;
  const effectiveToken = isExpired ? null : sessionToken;

  // Diagnostics: count how many users hit the stall path (network, not auth)
  const stallReportedRef = useRef(false);
  useEffect(() => {
    if (connectionStalled && !stallReportedRef.current) {
      stallReportedRef.current = true;
      console.warn("[session] Convex query stalled >20s — keeping session, showing reconnect banner");
      posthog.capture("session_connection_stalled");
    }
  }, [connectionStalled]);

  // Subscription data for PostHog tracking
  const subscription = useQuery(
    api.subscriptions.getSubscription,
    effectiveToken ? { sessionToken: effectiveToken } : "skip"
  );

  const expirePlan = useMutation(api.telegramAuthQueries.expireplan);

  // If user has an expired plan, persist downgrade to DB
  const expiredRef = useRef(false);
  useEffect(() => {
    if (paymentsEnabled && user && user.planExpiresAt && user.planExpiresAt < Date.now() && user.plan === "free" && effectiveToken && !expiredRef.current) {
      expiredRef.current = true;
      expirePlan({ sessionToken: effectiveToken }).catch(() => {});
    }
  }, [paymentsEnabled, user, effectiveToken, expirePlan]);

  const logout = useCallback(() => {
    Sentry.setUser(null);
    // Reset lives here, not at the call sites. Without it the next person to
    // sign in on this browser inherits the previous distinct_id and PostHog
    // merges both e-mails into a single person, so every per-user report
    // silently attributes one account's actions to another. Only the user-menu
    // path used to reset; the four others (settings, chat ×2, and any future
    // one) did not — 10.5% of editor events sat on contaminated persons.
    posthog.reset();
    clearSessionCookie();
    window.location.href = "/api/auth/signout";
  }, []);

  // Auto-logout ONLY when the server explicitly rejected the token — clear stale
  // cookie so user isn't stuck on a loading spinner forever (middleware sees
  // cookie → redirects to /new → Convex says expired → spinner → repeat).
  // Connection stalls intentionally do NOT land here: the session cookie stays,
  // Convex keeps reconnecting, and the reconnect banner covers the UX.
  const autoLogoutFiredRef = useRef(false);
  useEffect(() => {
    if (isExpired && !autoLogoutFiredRef.current) {
      autoLogoutFiredRef.current = true;
      console.warn("[session] Session expired, clearing cookie and redirecting to landing");
      Sentry.setUser(null);
      clearSessionCookie();
      window.location.href = "/";
    }
  }, [isExpired, user]);

  const isLoading = effectiveToken !== null && user === undefined;
  const isAuthenticated = !!user && !isExpired;

  // Identify user in PostHog when loaded
  // Use email as distinct_id (not Convex _id) so person merge works
  // reliably with session recordings on self-hosted PostHog
  const identifiedRef = useRef<string | null>(null);
  const signInFiredRef = useRef(false);
  useEffect(() => {
    if (user && user.email && identifiedRef.current !== user.email) {
      identifiedRef.current = user.email;
      Sentry.setUser({
        id: user._id,
        email: user.email,
        username: user.name,
      });

      posthog.identify(user.email, {
        name: user.name,
        email: user.email,
        convex_id: user._id,
        is_pro: user.plan === "plus",
        plan: user.plan,
        // UTM attribution
        utm_source: user.utmSource ?? null,
        utm_medium: user.utmMedium ?? null,
        utm_campaign: user.utmCampaign ?? null,
        utm_content: user.utmContent ?? null,
        utm_term: user.utmTerm ?? null,
      });

      // First-touch attribution + registration date (immutable)
      posthog.people.set_once({
        initial_utm_source: user.utmSource ?? null,
        initial_utm_medium: user.utmMedium ?? null,
        initial_utm_campaign: user.utmCampaign ?? null,
        registration_date: new Date(user._creationTime).toISOString(),
      });

      // Fire signed_up event for brand-new registrations
      if (document.cookie.includes("is_new_user=1")) {
        posthog.capture("signed_up", {
          auth_provider: user.authProvider,
          had_onboarding: localStorage.getItem("onboarding_seen") === "1",
          utm_source: user.utmSource ?? null,
          utm_medium: user.utmMedium ?? null,
          utm_campaign: user.utmCampaign ?? null,
        });
        // Delete the cookie so event fires only once
        document.cookie =
          "is_new_user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      } else if (!signInFiredRef.current) {
        signInFiredRef.current = true;
        const theme = localStorage.getItem("theme") ?? "dark";
        posthog.capture("signed_in", {
          auth_provider: user.authProvider,
          plan: user.plan,
          theme,
        });
        posthog.setPersonProperties({ theme });
      }

    }
    if (isExpired && identifiedRef.current) {
      identifiedRef.current = null;
      posthog.reset();
    }
  }, [user, isExpired]);

  // Check for auth error cookie (set by callback route on failure)
  useEffect(() => {
    if (document.cookie.includes("auth_error=")) {
      const match = document.cookie.match(/auth_error=([^;]*)/);
      if (match) {
        posthog.capture("auth_callback_failed", {
          reason: decodeURIComponent(match[1]),
        });
        document.cookie =
          "auth_error=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update is_pro reactively when plan changes (promo redeemed, subscription purchased)
  useEffect(() => {
    if (user && identifiedRef.current === user.email) {
      posthog.people.set({
        is_pro: user.plan === "plus",
        plan: user.plan,
      });
    }
  }, [user?.plan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track paid subscription status in PostHog person properties
  useEffect(() => {
    if (user && identifiedRef.current === user.email && subscription !== undefined) {
      const hasPaid = subscription !== null &&
        (subscription.status === "active" || subscription.status === "cancelled");
      posthog.people.set({
        has_paid_subscription: hasPaid,
        subscription_status: subscription?.status ?? "none",
      });
    }
  }, [user, subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  const rawUser = isExpired ? null : (user ?? null);

  const value: SessionContextValue = {
    user: rawUser,
    isLoading,
    isAuthenticated,
    sessionToken: effectiveToken,
    connectionStalled,
    logout,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
}
